import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { useConnectWordPress } from '@/hooks/useWordPress';
import { formatApiError } from '@/services/api';
import { useUnsavedGuard } from '@/hooks/useUnsavedGuard';

interface WordPressConnectModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConnected: (siteUrl: string, wpUsername: string) => void;
}

/**
 * WordPress site connect modal.
 *
 * Collects site URL + Application Password credentials (v1 scope — no
 * WordPress.com OAuth, per SPEC.md). Submits to `POST /api/wordpress/connect`,
 * which health-probes the site BEFORE persisting it — a failed probe comes
 * back as a specific 4xx (bad app password / REST disabled / unreachable),
 * surfaced inline here rather than a generic toast (CLAUDE.md rule 15).
 */
export const WordPressConnectModal = ({ open, onOpenChange, onConnected }: WordPressConnectModalProps) => {
  const { t } = useTranslation('settings');
  const [siteUrl, setSiteUrl] = useState('');
  const [username, setUsername] = useState('');
  const [applicationPassword, setApplicationPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [inlineError, setInlineError] = useState<string | null>(null);

  const connectMutation = useConnectWordPress();

  // Unsaved-work guard: credential fields are the whole point of this modal —
  // any content while open counts as meaningful unsaved input.
  useUnsavedGuard(
    open && (siteUrl.trim().length > 0 || username.trim().length > 0 || applicationPassword.trim().length > 0),
    'wordpress-connect-modal'
  );

  const resetAndClose = () => {
    setSiteUrl('');
    setUsername('');
    setApplicationPassword('');
    setShowPassword(false);
    setInlineError(null);
    onOpenChange(false);
  };

  const handleSubmit = async () => {
    if (!siteUrl.trim() || !username.trim() || !applicationPassword.trim()) {
      setInlineError(
        t('wordpress_connect_error_required', { defaultValue: 'Site URL, username, and application password are all required.' })
      );
      return;
    }

    setInlineError(null);

    try {
      const channel = await connectMutation.mutateAsync({
        siteUrl: siteUrl.trim(),
        username: username.trim(),
        applicationPassword: applicationPassword.trim(),
      });
      onConnected(channel.siteUrl, channel.wpUsername);
      resetAndClose();
    } catch (err) {
      // Backend surfaces specific health-probe failures (bad app password,
      // REST API blocked, host unreachable) via `message` — show it inline.
      setInlineError(formatApiError(err).message);
    }
  };

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next) resetAndClose();
        else onOpenChange(next);
      }}
      title={t('wordpress_connect_modal_title', { defaultValue: 'Connect WordPress Site' })}
      size="sm"
      footer={
        <div className="flex justify-end gap-3">
          <button
            onClick={resetAndClose}
            disabled={connectMutation.isPending}
            className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 transition-colors"
          >
            {t('settings_modal_cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={connectMutation.isPending || !siteUrl.trim() || !username.trim() || !applicationPassword.trim()}
            className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-[#21759B] hover:bg-[#1a5c7a] disabled:opacity-50 transition-colors flex items-center gap-2"
          >
            {connectMutation.isPending && <Icon name={ICONS.LOADER} size="sm" className="animate-spin" ariaHidden />}
            {connectMutation.isPending
              ? t('wordpress_connect_connecting', { defaultValue: 'Connecting...' })
              : t('settings_channel_button_connect')}
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          {t('wordpress_connect_modal_description', {
            defaultValue: 'Connect a self-hosted WordPress site using an Application Password. WordPress.com sites are not supported yet.',
          })}
        </p>

        <div>
          <label htmlFor="wp-site-url" className="block text-sm font-medium text-gray-700 mb-1">
            {t('wordpress_connect_site_url_label', { defaultValue: 'Site URL' })}
          </label>
          <Input
            id="wp-site-url"
            type="text"
            inputMode="url"
            value={siteUrl}
            onChange={(e) => setSiteUrl(e.target.value)}
            placeholder={t('wordpress_connect_site_url_placeholder', { defaultValue: 'https://yourblog.com' })}
            autoFocus
          />
        </div>

        <div>
          <label htmlFor="wp-username" className="block text-sm font-medium text-gray-700 mb-1">
            {t('wordpress_connect_username_label', { defaultValue: 'Username' })}
          </label>
          <Input
            id="wp-username"
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder={t('wordpress_connect_username_placeholder', { defaultValue: 'admin' })}
          />
        </div>

        <div>
          <label htmlFor="wp-app-password" className="block text-sm font-medium text-gray-700 mb-1">
            {t('wordpress_connect_app_password_label', { defaultValue: 'Application Password' })}
          </label>
          <div className="relative">
            <input
              id="wp-app-password"
              type={showPassword ? 'text' : 'password'}
              value={applicationPassword}
              onChange={(e) => setApplicationPassword(e.target.value)}
              placeholder={t('wordpress_connect_app_password_placeholder', { defaultValue: 'xxxx xxxx xxxx xxxx xxxx xxxx' })}
              className="w-full px-4 py-2.5 pr-10 rounded-lg border border-gray-200 focus:outline-none focus:border-gray-400 transition-colors text-sm"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
              aria-label={showPassword ? t('settings_password_hide') : t('settings_password_show')}
            >
              <Icon name={showPassword ? ICONS.EYE_SLASH : ICONS.EYE} size="sm" ariaHidden />
            </button>
          </div>
        </div>

        <div className="flex items-start gap-2 rounded-lg bg-soft-mint p-3">
          <Icon name={ICONS.INFO} size="sm" color="text-dark-teal" ariaHidden className="mt-0.5 flex-shrink-0" />
          <p className="text-xs text-dark-teal">
            {t('wordpress_connect_info_text', {
              defaultValue: 'Create an Application Password in your WordPress admin under Users > Profile > Application Passwords.',
            })}{' '}
            <a
              href="https://developer.wordpress.org/advanced-administration/security/application-passwords/"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium underline hover:no-underline"
            >
              {t('wordpress_connect_info_link', { defaultValue: 'Learn how' })}
            </a>
          </p>
        </div>

        {inlineError && (
          <div className="flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 p-3">
            <Icon name={ICONS.WARNING} size="sm" color="text-red-600" ariaHidden className="mt-0.5 flex-shrink-0" />
            <p className="text-xs text-red-700">{inlineError}</p>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default WordPressConnectModal;
